import { Button, CheckboxFormField, ClassName, Form, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import { useToastOpen } from 'contexts/ToastContext';
import { usePositionUpdate } from 'hooks';
import { t } from 'i18next';
import { isEqual } from 'lodash';
import { MutableRefObject, useRef } from 'react';

export interface IJsaReportsFormData {
  createSafetyAnalysisReports: boolean;
  manageSafetyAnalysisReports: boolean;
}

export interface IJsaReportsFormProps {
  positionId: string;
  createSafetyAnalysisReports: boolean;
  manageSafetyAnalysisReports: boolean;
  onSubmit: () => void;
  className?: ClassName;
}

const JsaReportsForm = ({
  className,
  positionId,
  createSafetyAnalysisReports,
  manageSafetyAnalysisReports,
  onSubmit,
}: IJsaReportsFormProps) => {
  const [, update] = usePositionUpdate();
  const toast = useToastOpen();
  const initialFormData: MutableRefObject<IJsaReportsFormData> = useRef({
    createSafetyAnalysisReports,
    manageSafetyAnalysisReports,
  });

  const handleFormSubmit = async (formData: IJsaReportsFormData) => {
    if (!isEqual(initialFormData, formData)) {
      try {
        await update({
          id: positionId,
          createSafetyAnalysisReports: formData.createSafetyAnalysisReports,
          manageSafetyAnalysisReports: formData.createSafetyAnalysisReports
            ? formData.manageSafetyAnalysisReports
            : false,
        });
      } catch (error) {
        toast({ label: t('Something went wrong. Please try again later.') });
      }
    }
    onSubmit();
  };

  const performLabel = () => (
    <>
      <h4>{t('Create JSA Reports')}</h4>
      <h6>{t('Allow the user to create JSA reports. They can also edit and delete their own reports.')}</h6>
    </>
  );

  const createLabel = () => (
    <>
      <h4>{t('Manage JSA Reports')}</h4>
      <h6>{t('Allow the user to edit and delete reports created by others.')}</h6>
    </>
  );

  const renderFormFields = (form: IFormRender<IJsaReportsFormData>) => (
    <div className={'m-5 pt-8'}>
      <CheckboxFormField name={'createSafetyAnalysisReports'} form={form} label={performLabel()} />
      {form.state.data.createSafetyAnalysisReports && (
        <DashboardRibbon theme={Theme.LIGHT}>
          <CheckboxFormField name={'manageSafetyAnalysisReports'} form={form} label={createLabel()} />
        </DashboardRibbon>
      )}
      <Button onClick={form.handleSubmit} type="primary" className={'mt-4'}>
        {t('Update')}
      </Button>
    </div>
  );

  const classes = classNames('jsa-reports-form', className);

  return (
    <Form render={renderFormFields} onSubmit={handleFormSubmit} data={initialFormData.current} className={classes} />
  );
};

export default JsaReportsForm;
